'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { Space_Grotesk, Outfit } from 'next/font/google'
import { BackgroundBeams } from '../_components/_ui/background-beams'
import Footer from '../_components/_ui/Footer'

const spaceGrotesk = Space_Grotesk({ 
  subsets: ['latin'],
  variable: '--font-space-grotesk',
  display: 'swap'
})

const outfit = Outfit({ 
  subsets: ['latin'],
  variable: '--font-outfit',
  display: 'swap'
})

const aboutPages = [
    { 
        name: 'Features',
        href: '/about/features',
        description: "Discover all the amazing tools we've built to help you share content your way. ✨",
        icon: "🚀",
        color: "bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20"
    },
    { 
        name: 'Pricing',
        href: '/about/pricing',
        description: "Simple, transparent pricing that grows with you. No hidden fees, promise! 💰",
        icon: "💎",
        color: "bg-purple-500/10 hover:bg-purple-500/20 border-purple-500/20"
    },
    { 
        name: 'Roadmap',
        href: '/about/roadmap',
        description: "Take a peek at what we're building next. We're just getting started! 🗺️",
        icon: "🔮",
        color: "bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/20"
    },
    { 
        name: 'Changelog',
        href: '/about/changelog',
        description: "See our latest updates and improvements. We ship fast and often! 🚢",
        icon: "📝",
        color: "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20"
    },
    { 
        name: 'Showcase Profiles',
        href: '/about/showcase',
        description: "Check out amazing profiles built with Autofoc.us. Get inspired! ✨",
        icon: "🌟",
        color: "bg-pink-500/10 hover:bg-pink-500/20 border-pink-500/20"
    },
    { 
        name: 'Templates',
        href: '/about/templates',
        description: "Start with a template and make it your own. Beautiful designs ready to go! 🎨",
        icon: "📱",
        color: "bg-indigo-500/10 hover:bg-indigo-500/20 border-indigo-500/20"
    },
]

export default function AboutNav() {
    const pathname = usePathname()

    return (
        <div className="min-h-screen w-full relative flex flex-col">
            <BackgroundBeams />
            <div className="relative z-10 flex-grow">
                {/* Hero Section */}
                <div className="pt-16 pb-8 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto">
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="font-space-grotesk text-2xl sm:text-3xl font-bold text-white leading-tight"
                        >
                            About Autofoc.us
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-sm text-gray-400 mt-2"
                        >
                            Building the future of content sharing, one block at a time.
                        </motion.p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {aboutPages.map((page, index) => (
                            <motion.div
                                key={page.name}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Link href={page.href}>
                                    <div className={`p-6 rounded-xl border backdrop-blur-sm transition-all duration-300 ${page.color}`}>
                                        <div className="flex items-center gap-4">
                                            <span className="text-3xl">{page.icon}</span>
                                            <div>
                                                <h2 className="text-lg font-semibold text-white mb-2">
                                                    {page.name}
                                                </h2>
                                                <p className="text-sm text-gray-300">
                                                    {page.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>

                    {/* Mission Banner */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className="mt-12 p-6 rounded-xl border border-indigo-500/20 bg-indigo-500/10 backdrop-blur-sm"
                    >
                        <h3 className="text-lg font-semibold text-white mb-2">
                            Our Mission 🌈
                        </h3>
                        <p className="text-sm text-gray-300">
                            At Autofoc.us, we're revolutionizing how creators share their content. 
                            By combining the simplicity of a Linktree™ with the power of modern web technologies, 
                            we're building a platform that helps creators showcase their work in a beautiful, 
                            organized, and engaging way.
                        </p>
                        <p className="text-xs text-gray-400 mt-1 italic">
                            *linktree™ is a trademark of Linktree. Autofoc.us is not affiliated with, endorsed by, or sponsored by Linktree.
                        </p>
                    </motion.div>
                </div>
            </div>
            <Footer />
        </div>
    )
} 